import { API_URL } from "./apiRoutes";
import { originalFetch } from "./authInterceptor";
import { logApiError } from "../utils/apiErrorMessages";

/**
 * Lista endpointów, które nie powinny mieć automatycznie dodawanego parametru userLanguage.
 *
 * Powody wykluczenia mogą obejmować:
 * - Endpointy zwracające dane strukturalne (np. dostępność w kalendarzu)
 * - Endpointy, które nie obsługują wielojęzyczności po stronie backendu
 * - Endpointy, gdzie język jest określany w inny sposób
 *
 * Format: Podaj fragment ścieżki URL, który jednoznacznie identyfikuje endpoint.
 */
const URL_LANGUAGE_PARAM_EXCLUSIONS: string[] = [
  // "/EAktywni/attractions/calendar",
  // "/EAktywni/exercises/datesAndFacility"
  // Dodaj kolejne endpointy tutaj w miarę potrzeb
];

const isUrlExcludedFromLanguageParam = (url: string): boolean => {
  return URL_LANGUAGE_PARAM_EXCLUSIONS.some((exclusion) => url.includes(exclusion));
};

const addLanguageParamToUrl = (url: string) => {
  // Sprawdź, czy URL jest na liście wykluczeń
  if (isUrlExcludedFromLanguageParam(url)) {
    return "";
  }

  const localStorageLng = localStorage.getItem("i18nextLng");
  if (localStorageLng && localStorageLng !== "PL") {
    const separator = url.includes("?") ? "&" : "?";
    return `${separator}userLanguage=${localStorageLng}`;
  } else {
    return "";
  }
};

const parseJsonAndLog = async (response: Response, method: string, url: string) => {
  const json = await response.json();
  if (json?.isError) {
    logApiError(method, url, json);
  }
  return json;
};

export const post = async (url: string, data = {}, signal?: AbortSignal) => {
  const response = await fetch(getFullURL(url), {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    signal,
    body: JSON.stringify(data),
  });
  return await parseJsonAndLog(response, "POST", url);
};

export const postMultipart = async (url: string, data: FormData, signal?: AbortSignal) => {
  const response = await fetch(getFullURL(url), {
    method: "POST",
    signal,
    body: data,
  });
  return await parseJsonAndLog(response, "POST", url);
};

export const get = async (url: string, signal?: AbortSignal) => {
  const response = await fetch(getFullURL(url), {
    method: "GET",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    signal,
  });
  return await parseJsonAndLog(response, "GET", url);
};

export const del = async (url: string, data = {}, signal?: AbortSignal) => {
  const response = await fetch(getFullURL(url), {
    method: "DELETE",
    mode: "cors",
    signal,
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify(data),
  });
  return await parseJsonAndLog(response, "DELETE", url);
};

export const put = async (url: string, data = {}, signal?: AbortSignal) => {
  const response = await fetch(getFullURL(url), {
    method: "PUT",
    mode: "cors",
    signal,
    headers: {
      "Content-Type": "application/json",
    },
    redirect: "follow",
    body: JSON.stringify(data),
  });
  return await parseJsonAndLog(response, "PUT", url);
};

const getFullURL = (url: string) => `${API_URL}${url}${addLanguageParamToUrl(url)}`;

export const postWithToken = async (url: string, data = {}, token: string, signal?: AbortSignal) => {
  const response = await originalFetch(getFullURL(url), {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "X-Auth-User-Token": token,
    },
    redirect: "follow",
    signal,
    body: JSON.stringify(data),
  });
  return await parseJsonAndLog(response, "POST", url);
};
